/**
 * This module contains hard-coded client-specific configurations in
 * one place. wizard.tsx looks up the configuration for the current
 * client during render, and passes it to all components in the
 * context.
 */

/**
 * Sentinel value for fields where user has selected "Other" from
 * dropdown.
 */
export const OTHER = "other"
/**
 * Sentinel value for fields where user has selected "Other" from
 * dropdown.
 */
export type OTHER = typeof OTHER

/**
 * Sentinel value for fields where user has selected "N/A" from
 * dropdown.
 */
export const NOT_APPLICABLE = "not-applicable"
/**
 * Sentinel value for fields where user has selected "N/A" from
 * dropdown.
 */
export type NOT_APPLICABLE = typeof NOT_APPLICABLE

export interface IClientConfig {
  fillerConfig: IContactType
  contactTypes: ContactTypeArray
  stepNames: readonly string[]
  qualifierReasons?: readonly IQualifierReason[]
  contactExclusivity: ContactTypeName[] | false
  /**
   * Field above "Submitter Name" just for OHCA.
   */
  hasParentCompanyName?: boolean
  insurerTypeRequired: boolean
  hasFein: boolean
  feinRequired: boolean
  /**
   * Optional array of license types to show for this client, below FEIN.
   */
  licenseTypes?: ILicenseType[]
  maxSelectedLicenseTypes?: number
  showLicensedEntityName?: boolean
  /**
   * Show (true) or hide (false) the submitter type dropdown. Default true.
   */
  showSubmitterType: boolean
  /**
   * Submitter type dropdown options shown when showSubmitterType is true.
   */
  submitterTypes: InsurerTypeName[]
  /**
   * Show (true) or hide (false) the participant type dropdown. Default false.
   * Replaces submitter type dropdown for OHCA.
   */
  showParticipantType?: boolean
  /**
   * Optional configuration, shown below license types and/or FEIN.
   * Market categories are CA-specific.
   */
  marketCategories?: string[]
  /**
   * Enables "registration number" field for PBM, below the PBM organization name field.
   * Colorado request in DO-562.
   */
  showPbmRegistrationNumber?: boolean
  /**
   * Label for "registration number" field for PBM, below the PBM organization name field.
   * Colorado request in DO-562.
   */
  pbmRegistrationNumberLabel?: string
  /**
   * Enables "qualification date" field, after user chooses "yes" for qualification.
   * Colorado request in DO-562.
   */
  showQualificationDateField?: boolean
  /**
   * Label for "qualification date" field, after user chooses "yes" for qualification.
   * Colorado request in DO-562.
   */
  qualificationDateFieldLabel?: string
  /**
   * Label for license type dropdown. Default "License Type".
   * OHCA request in DO-714 (requested "License Issuer" instead of "License Type").
   */
  licenseTypeFieldLabel?: string
  /**
   * Tooltip for license type dropdown, hidden when undefined.
   * OHCA request in DO-714.
   */
  licenseTypeTooltip?: string
  tpaUseQuestion: ICarveOutUseQuestion
  pbmUseQuestion: ICarveOutUseQuestion
  carveOutPayerUseQuestion: ICarveOutUseQuestion
  /**
   * Show (true) or hide (false) the self-insured client info form for submitters that are a TPA. Default false.
   * MN request in DO-270.
   */
  showTpaSelfInsuredForm?: boolean
  /**
   * Define the available contact types by submitter type.
   * If a submitter type is not provided, all contact types are available regardless of submitter type.
   * IN request in DO-1052.
   */
  contactTypesBySubmitterType?: Partial<Record<InsurerTypeName, ContactTypeArray>>
}

/**
 * Get the contact types for a given submitter type. If no submitter type is provided, all contact types are returned.
 */
export function getContactTypes(config: IClientConfig, submitterType?: InsurerTypeName): ContactTypeArray {
  if (!submitterType) {
    return config.contactTypes;
  }

  return config.contactTypesBySubmitterType?.[submitterType] ?? config.contactTypes;
}

export interface IInsurerType {
  value: InsurerTypeName
  label: string
}

/**
 * Do not change (use InsurerTypeLabels to change the display name):
 */
export const InsurerType = {
  InsuranceCompanyLegacy: "Insurance Company",
  InsuranceCompany: "Health Plan / Insurer",
  ThirdPartyAdministrator: "Third-Party Administrator",
  PharmacyBenefitsManager: "Pharmacy Benefits Manager",
  GovernmentProvider: "Government Provider",
  BehavioralHealthCarveOutPayer: "Behavioral Health Carve-Out Payer",
  Provider: "Provider",
  Employer: "Employer",
} as const;

export type InsurerTypeName = (typeof InsurerType)[keyof typeof InsurerType];
export type InsurerTypeLabels = Record<InsurerTypeName, string>;

export const InsurerTypeLabels = {
  [InsurerType.InsuranceCompanyLegacy]: "Insurance Company",
  [InsurerType.InsuranceCompany]: "Health Plan / Insurer",
  [InsurerType.ThirdPartyAdministrator]: "Third-Party Administrator",
  [InsurerType.PharmacyBenefitsManager]: "Pharmacy Benefits Manager",
  [InsurerType.GovernmentProvider]: "Government Provider",
  [InsurerType.BehavioralHealthCarveOutPayer]: "Behavioral Health Carve-Out Payer",
  [InsurerType.Provider]: "Provider",
  [InsurerType.Employer]: "Employer",
} satisfies InsurerTypeLabels;

const DEFAULT_SUBMITTER_TYPES: InsurerTypeName[] = [
  InsurerType.InsuranceCompany,
  InsurerType.ThirdPartyAdministrator,
  InsurerType.PharmacyBenefitsManager,
  InsurerType.GovernmentProvider,
  InsurerType.BehavioralHealthCarveOutPayer,
]

/**
 * Note that the label should only really be used for values with special meaning,
 * like "other", to make the CSV export simpler and record the exact legal language
 * chosen by the user as it appeared on the page.
 */
export interface IQualifierReason {
  value: string
}

export type ContactTypeArray = [ContactTypeName, IContactType][]

export interface IContactType {
  title: string
  description: string
  required: boolean
  showNotApplicable: boolean
  showPlans: boolean
  refFileTypeId: number | null
}

export const CONTACT_TYPE_NAMES = [
  "filler",
  // "program_management",
  "program_compliance",
  "legal_advisory",
  "sftp",
  "lockbox_enrollment_submissions",
  "crisp_enrollment_submissions",
  "enrollment_submissions",
  "medical_claims_submission",
  "pharmacy_claims_submission",
  "dental_claims_submissions",
  "provider_file_submissions",
  "member_identifier_submissions",
  "technical_support_1",
  "technical_support_2",
  // Custom OHCA contact types:
  "business",
  "regulatory",
  "technical",
  // DO-199 HCAI NCP file types:
  "annual_payments",
  "pharmacy_rebates",
  "capitation",
  // DO-562 Colorado contact types:
  "alternative_payment_model",
  "control_total",
  "pbm_contract",
  "prescription_affordability",
  "value_based_contract",
  "apm_contract",
  // DO-270 MN contact type:
  "non_claims_payments",
  // DO-1052 IN contact types:
  "commissions_and_brokerage",
  "provider_price_transparency",
  // DO-1131 VA contact type:
  "primary_data_supplier",
] as const

export type ContactTypeName = typeof CONTACT_TYPE_NAMES[number]

export interface ILicenseType {
  name: LicenseTypeName
  label: string
  show_license_number: boolean
  require_license_number: boolean
  require_custom_name?: boolean
}

/**
 * Constants that must never change:
 */
export type LicenseTypeName =
  | "DMHC"
  | "CDI"
  | "Self-Insured"
  | OTHER

/**
 * Do not change:
 */
export const ParticipantTypes = [
  "Voluntary",
  "Mandatory",
] as const
export type ParticipantType = typeof ParticipantTypes[number]

/**
 * Currently only used by OHCA. Names must never change, but labels
 * may change.
 */
const defaultLicenseTypes: ILicenseType[] = [
  {
    name: "DMHC",
    label: "DMHC",
    show_license_number: true,
    require_license_number: true,
  },
  {
    name: "CDI",
    label: "CDI",
    show_license_number: true,
    require_license_number: true,
  },
  {
    name: "Self-Insured",
    label: "Self-Insured",
    show_license_number: false,
    require_license_number: false,
  },
  {
    name: OTHER,
    label: "Other (Specify Type)",
    show_license_number: true,
    require_license_number: false,
    require_custom_name: true,
  },
]

// /**
//  * Get a license type by name. Throws error if name is invalid.
//  *
//  * @param {string} name the name
//  * @returns {object} the license type
//  */
// export function getLicenseType(name: string): ILicenseType {
//   const licenseType = LicenseTypes.find((lt) => lt.name === name)
//   if (!licenseType) {
//     throw new Error(`License Type ${name} not found`)
//   }
//   return licenseType
// }

const defaultStepNames: readonly string[] = [
  "Submitter",
  "Contacts",
  "Submissions",
  "Review",
]

const FILLER_DEFAULT: IContactType = {
  title: "Registrant",
  description: "Please provide the following information about the person completing this form.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

// const PROGRAM_MANAGEMENT_DEFAULT: IContactType = {
//   title: "Program Oversight/Management",
//   description:
//     "Please provide the following information regarding the person responsible for program oversight / management.",
//   required: true,
//   showNotApplicable: false,
// }

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const PROGRAM_COMPLIANCE_DEFAULT: IContactType = {
  title: "Program Compliance",
  description: "Please provide the following information regarding the person responsible for program compliance.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const LEGAL_ADVISORY_DEFAULT: IContactType = {
  title: "Legal Advisory",
  description: "Please provide the following information regarding the person responsible for legal advisory.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const SFTP_DEFAULT: IContactType = {
  title: "SFTP Connectivity",
  description:
  "Please provide the following information regarding the person responsible for establishing connectivity with Onpoint's SFTP server.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const LOCKBOX_DEFAULT: IContactType = {
  title: "Lockbox Eligibility",
  description:
    "Please provide the following information regarding the person responsible for Lockbox enrollment submissions.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=null, showPlans=false
 */
const CRISP_ENROLLMENT_DEFAULT: IContactType = {
  title: "CRISP Demographic",
  description:
    "Please provide the following information regarding the person responsible for CRISP enrollment submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=1, showPlans=false
 */
const ENROLLMENT_DEFAULT: IContactType = {
  title: "Eligibility",
  description:
    "Please provide the following information regarding the person responsible for enrollment submissions.",
  required: true,
  showNotApplicable: true,
  refFileTypeId: 1,
  showPlans: false,
}

/**
 * CRISP_ENROLLMENT_DEFAULT with N/A option disabled.
 */
const ENROLLMENT_REQUIRED: IContactType = {
  ...ENROLLMENT_DEFAULT,
  showNotApplicable: false,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=2, showPlans=false
 */
const MEDICAL_DEFAULT: IContactType = {
  title: "Medical Claims",
  description:
    "Please provide the following information regarding the person responsible for medical claims submissions.",
  required: true,
  showNotApplicable: true,
  refFileTypeId: 2,
  showPlans: false,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=3, showPlans=false
 */
const PHARMACY_DEFAULT: IContactType = {
  title: "Pharmacy Claims",
  description:
    "Please provide the following information regarding the person responsible for pharmacy claims submissions.",
  required: true,
  showNotApplicable: true,
  refFileTypeId: 3,
  showPlans: false,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=4, showPlans=false
 */
const DENTAL_DEFAULT: IContactType = {
  title: "Dental Claims",
  description:
    "Please provide the following information regarding the person responsible for dental claims submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 4,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=5, showPlans=false
 */
const PROVIDER_DEFAULT: IContactType = {
  title: "Provider",
  description:
    "Please provide the following information regarding the person responsible for provider file submissions.",
  required: true,
  showNotApplicable: false,
  refFileTypeId: 5,
  showPlans: false,
}

/**
 * PROVIDER_DEFAULT with N/A allowed.
 */
const PROVIDER_OPTIONAL: IContactType = {
  ...PROVIDER_DEFAULT,
  showNotApplicable: true,
}

/**
 * required=true, showNotApplicable=true, refFileTypeId=6, showPlans=false
 */
const MEMBER_DEFAULT: IContactType = {
  title: "Member Identifier",
  description:
  "Please provide the following information regarding the person responsible for member identifier submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 6,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const BUSINESS_DEFAULT: IContactType = {
  title: "Business Contact",
  description:
    "Please provide the contact information for a business contact for submission issues.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const REGULATORY_DEFAULT: IContactType = {
  title: "Regulatory Contact",
  description:
    "Provide the contact information for regulatory questions.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

/**
 * required=true, showNotApplicable=false, refFileTypeId=null, showPlans=false
 */
const TECHNICAL_DEFAULT: IContactType = {
  title: "Technical Contact",
  description:
    "Provide the contact information for technical questions related to each data file type.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

const ALTERNATIVE_PAYMENT_MODEL_DEFAULT: Readonly<IContactType> = {
  title: "Alternative Payment Model",
  description: "Provide the contact information for the person responsible for alternative payment model submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 25,
}

const CONTROL_TOTAL_DEFAULT: Readonly<IContactType> = {
  title: "Control Total",
  description: "Provide the contact information for the person responsible for control total submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 28,
}

const CAPITATION_DEFAULT: Readonly<IContactType> = {
  title: "Capitation",
  description: "Provide the contact information for the person responsible for capitation submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 24,
}

const PHARMACY_REBATES_DEFAULT: Readonly<IContactType> = {
  title: "Pharmacy Rebates",
  description: "Provide the contact information for the person responsible for pharmacy rebates submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: true,
  refFileTypeId: 19,
}

const PBM_CONTRACT_DEFAULT: Readonly<IContactType> = {
  title: "PBM Contract",
  description: "Provide the contact information for the person responsible for PBM contract submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 29,
}

const PRESCRIPTION_AFFORDABILITY_DEFAULT: Readonly<IContactType> = {
  title: "Prescription Affordability",
  description: "Provide the contact information for the person responsible for prescription affordability submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 30,
}

const VALUE_BASED_CONTRACT_DEFAULT: Readonly<IContactType> = {
  title: "Value Based Contract",
  description: "Provide the contact information for the person responsible for value based contract submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 31,
}

const APM_CONTRACT_DEFAULT: Readonly<IContactType> = {
  title: "APM Contract",
  description: "Provide the contact information for the person responsible for APM contract submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 27,
}

const COMMISSIONS_AND_BROKERAGE_DEFAULT: Readonly<IContactType> = {
  title: "Commissions and Brokerage",
  description:
    "Please provide the following information regarding the person responsible for commissions and brokerage file submissions.",
  required: true,
  showNotApplicable: true,
  showPlans: false,
  refFileTypeId: 33,
}

const PROVIDER_PRICE_TRANSPARENCY_DEFAULT: Readonly<IContactType> = {
  title: "Provider Price Transparency",
  description:
    "Please provide the following information regarding the person responsible for provider price transparency file submissions.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: 0,
}

const TECHNICAL_SUPPORT_DEFAULT: Readonly<IContactType> = {
  title: "Technical Support",
  description: "Provide the contact information for a contact for technical questions/support.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}

const PRIMARY_DATA_SUPPLIER_DEFAULT: Readonly<IContactType> = {
  title: "Primary Data Supplier",
  description: "Please provide the following information regarding the primary contact for your organization.",
  required: true,
  showNotApplicable: false,
  showPlans: false,
  refFileTypeId: null,
}
export interface ICarveOutUseQuestion {
  question: string
  options: readonly ICarveOutUseOption[]
}

export interface ICarveOutUseOption {
  value: string
  label: string
  /**
   * If true, this option won't be shown in the radio button group.
   * Previously available options should be hidden instead of deleted so labels are still visible when reviewing old forms.
   */
  hidden: boolean
  note?: string
  organizationNameTooltip?: string
}

/**
 * Duplicated in registration_form.rb
 */
export enum TPAUse {
  /**
   * New choice / clarification introduced in new version of the form.
   * Removed for non-MN clients in DO-693.
   */
  WE_ARE_THE_TPA = "we_are_the_tpa",
  /**
   * Old "yes" answer was ambiguous.
   */
  WE_HAVE_A_TPA = "yes",
  /**
   * Old "no" answer same as new answer.
   */
  NO_TPA = "no",
}

const DEFAULT_TPA_USE_QUESTION: ICarveOutUseQuestion = {
  question: "Is there an organization that is contracted with your organization as a third-party administrator (TPA)?",
  options: [
    {
      value: TPAUse.WE_ARE_THE_TPA,
      label: "Yes, we are a TPA.",
      hidden: true,
    },
    {
      value: TPAUse.WE_HAVE_A_TPA,
      label: "Yes, we have a TPA submitting data on our behalf.",
      note: "Please use the fields below to indicate the TPA submitting data on behalf of your health plan/insurer.",
      hidden: false,
      organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the TPA administering benefits on your behalf.",
    },
    {
      value: TPAUse.NO_TPA,
      label: "No, we do not have a TPA submitting on our behalf.",
      hidden: false,
    },
  ],
}

/**
 * Duplicated in registration_form.rb
 */
export enum PBMUse {
  /**
   * New choice / clarification introduced in new version of the form.
   * Removed for non-MN clients in DO-693.
   */
  WE_ARE_THE_PBM = "we_are_the_pbm",
  /**
   * Old "yes" answer was ambiguous.
   */
  WE_HAVE_A_PBM = "yes",
  /**
   * Old "no" answer same as new answer.
   */
  NO_PBM = "no",
}

const DEFAULT_PBM_USE_QUESTION: ICarveOutUseQuestion = {
  question: "Is there an organization that is contracted with your organization as a pharmacy benefits manager (PBM)?",
  options: [
    {
      value: PBMUse.WE_ARE_THE_PBM,
      label: "Yes, we are a PBM.",
      hidden: true,
    },
    {
      value: PBMUse.WE_HAVE_A_PBM,
      label: "Yes, we have a PBM submitting data on our behalf.",
      hidden: false,
      note: "Please use the fields below to indicate the PBM submitting data on behalf of your health plan/insurer.",
      organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the PBM administering benefits on your behalf.",
    },
    {
      value: PBMUse.NO_PBM,
      label: "No, we do not have PBM submitting on our behalf.",
      hidden: false,
    },
  ],
}

/**
 * Duplicated in registration_form.rb
 */
export enum CarveOutPayerUse {
  /**
   * New choice / clarification introduced in new version of the form.
   * Removed for non-MN clients in DO-693.
   */
  WE_ARE_THE_CARVE_OUT_PAYER = "we_are_the_carve_out_payer",
  /**
   * Old "yes" answer was ambiguous.
   */
  WE_HAVE_A_CARVE_OUT_PAYER = "yes",
  /**
   * Old "no" answer same as new answer.
   */
  NO_CARVE_OUT_PAYER = "no",
}

const DEFAULT_CARVE_OUT_PAYER_USE_QUESTION: ICarveOutUseQuestion = {
  question: "Is there an organization that is contracted with your organization as a behavioral health carve-out payer?",
  options: [
    {
      value: CarveOutPayerUse.WE_ARE_THE_CARVE_OUT_PAYER,
      label: "Yes, we are a behavioral health carve-out payer.",
      hidden: true,
    },
    {
      value: CarveOutPayerUse.WE_HAVE_A_CARVE_OUT_PAYER,
      label: "Yes, we have a behavioral health carve-out payer submitting data on our behalf.",
      hidden: false,
      note: "Please use the fields below to indicate the behavioral health carve-out payer submitting data on behalf of your health plan/insurer.",
      organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the behavioral health carve-out payer administering benefits on your behalf.",
    },
    {
      value: CarveOutPayerUse.NO_CARVE_OUT_PAYER,
      label: "No, we do not have a behavioral health carve-out payer submitting on our behalf.",
      hidden: false,
    },
  ],
}

const Alaska: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const California: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["technical_support_1", TECHNICAL_SUPPORT_DEFAULT],
    ["technical_support_2", {
      title: "Additional Technical Support",
      description: "Provide the contact information for a second contact for technical questions/support.",
      required: true,
      showNotApplicable: false,
      showPlans: false,
      refFileTypeId: null,
    }],
    ["enrollment_submissions", {
      ...ENROLLMENT_REQUIRED,
      description: "Select the plans for which your organization is responsible for submitting enrollment submissions and please provide the contact information of the person responsible for enrollment submissions.",
      showPlans: true,
    }],
    ["medical_claims_submission", {
      ...MEDICAL_DEFAULT,
      description: "Select the plans for which your organization is responsible for submitting medical claims submissions and please provide the contact information of the person responsible for medical claims submissions.",
      showPlans: true,
      showNotApplicable: true,
    }],
    ["pharmacy_claims_submission", {
      ...PHARMACY_DEFAULT,
      description: "Select the plans for which your organization is responsible for submitting pharmacy claims submissions and please provide the contact information of the person responsible for pharmacy claims submissions.",
      showPlans: true,
      showNotApplicable: true,
    }],
    ["dental_claims_submissions", {
      ...DENTAL_DEFAULT,
      description: "Select the plans for which your organization is responsible for submitting dental claims submissions and please provide the contact information of the person responsible for dental claims submissions.",
      showPlans: true,
      showNotApplicable: true,
    }],
    ["annual_payments", {
      title: "Annual Payments",
      description: "Select the plans for which your organization is responsible for submitting annual payments submissions and please provide the contact information of the person responsible for annual payments submissions.",
      required: true,
      showNotApplicable: true,
      showPlans: true,
      refFileTypeId: 23,
    }],
    ["pharmacy_rebates", {
      ...PHARMACY_REBATES_DEFAULT,
      description: "Select the plans for which your organization is responsible for submitting pharmacy rebates submissions and please provide the contact information of the person responsible for pharmacy rebates submissions.",
      showPlans: true,
    }],
    ["capitation", {
      ...CAPITATION_DEFAULT,
      description: "Select the plans for which your organization is responsible for submitting capitation submissions and please provide the contact information of the person responsible for capitation submissions.",
      showPlans: true,
    }],
    ["provider_file_submissions", {
      ...PROVIDER_DEFAULT,
      description: "Please provide the contact information for the person responsible for provider-file submissions.",
      showNotApplicable: false,
      showPlans: false,
    }],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: ["technical_support_1", "technical_support_2"],
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

// See requirements in DO-562
const Colorado: Readonly<IClientConfig> = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["legal_advisory", LEGAL_ADVISORY_DEFAULT],
    ["sftp", {
      ...SFTP_DEFAULT,
      showNotApplicable: true,
    }],
    ["enrollment_submissions", {
      ...ENROLLMENT_DEFAULT,
      showNotApplicable: false,
    }],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", {
      ...PROVIDER_DEFAULT,
      showNotApplicable: true,
    }],
    ["alternative_payment_model", ALTERNATIVE_PAYMENT_MODEL_DEFAULT],
    ["control_total", CONTROL_TOTAL_DEFAULT],
    ["capitation", CAPITATION_DEFAULT],
    ["pharmacy_rebates", PHARMACY_REBATES_DEFAULT],
    ["pbm_contract", PBM_CONTRACT_DEFAULT],
    ["prescription_affordability", PRESCRIPTION_AFFORDABILITY_DEFAULT],
    ["value_based_contract", VALUE_BASED_CONTRACT_DEFAULT],
    ["apm_contract", APM_CONTRACT_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  showPbmRegistrationNumber: true,
  pbmRegistrationNumberLabel: "Colorado PBM Registration Number",
  showQualificationDateField: true,
  qualificationDateFieldLabel: "What was the earliest date that your organization met the threshold for qualifying to submit data to the CO APCD?",
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Connecticut: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  // DO-135 - do not change values!
  qualifierReasons: [
    {
      value: "Our health plan provided coverage to fewer than 3,000 members enrolled in plans not otherwise excluded from the files that are offered or administered by the Reporting Entity on October 1 of this year and is therefore exempt from the data submission requirements set forth in this Policy and Procedure for the following calendar year.",
    },
    {
      value: "Our health plan does not include any of the following members: Connecticut residents, students enrolled in a student plan at a Connecticut college or university, or individuals covered by a health plan issued in Connecticut in the individual or small-group market (except to the extent that such health plan is a recognized, grandfathered plan that is exempt from the risk-adjustment requirements of Section 1343 of the federal Patient Protection and Affordable Care Act) regardless of their state of residence.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const CoveredCA: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["primary_data_supplier", PRIMARY_DATA_SUPPLIER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Demonstration: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Georgia: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  qualifierReasons: [
    // DO-136 - do not change values!
    {
      value: "Our health plan, health insurer, or public self-insured plan does not meet the required threshold of 1,000 covered lives in aggregate across all plans as of December 31st of the previous year.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const HealthShareExchange: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const DEFAULT_IN_CONTACT_TYPES: ContactTypeArray = [
  ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
  ["sftp", SFTP_DEFAULT],
  ["enrollment_submissions", ENROLLMENT_DEFAULT],
  ["medical_claims_submission", MEDICAL_DEFAULT],
  ["pharmacy_claims_submission", PHARMACY_DEFAULT],
  ["provider_file_submissions", PROVIDER_DEFAULT],
  ["commissions_and_brokerage", COMMISSIONS_AND_BROKERAGE_DEFAULT],
]

const Indiana: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  qualifierReasons: [
    // DO-137 - do not change values!
    {
      value: "Health plan does not meet the minimum threshold of 3,000 covered lives in the state of Indiana.",
    },
    {
      value: "Health plan does not have any books of business (or license) in the state of Indiana.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ...DEFAULT_IN_CONTACT_TYPES,
    ["provider_price_transparency", PROVIDER_PRICE_TRANSPARENCY_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: [...DEFAULT_SUBMITTER_TYPES, InsurerType.Provider, InsurerType.Employer],
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
  contactTypesBySubmitterType: {
    [InsurerType.InsuranceCompany]: DEFAULT_IN_CONTACT_TYPES,
    [InsurerType.ThirdPartyAdministrator]: DEFAULT_IN_CONTACT_TYPES,
    [InsurerType.PharmacyBenefitsManager]: DEFAULT_IN_CONTACT_TYPES,
    [InsurerType.GovernmentProvider]: DEFAULT_IN_CONTACT_TYPES,
    [InsurerType.BehavioralHealthCarveOutPayer]: DEFAULT_IN_CONTACT_TYPES,
    [InsurerType.Provider]: [
      ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
      ["provider_price_transparency", PROVIDER_PRICE_TRANSPARENCY_DEFAULT],
    ],
    [InsurerType.Employer]: [
      ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
      ["enrollment_submissions", ENROLLMENT_DEFAULT],
      ["medical_claims_submission", MEDICAL_DEFAULT],
      ["pharmacy_claims_submission", PHARMACY_DEFAULT],
      ["provider_file_submissions", PROVIDER_DEFAULT],
    ],
  },
}

const IntegratedHealthAssociation: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["member_identifier_submissions", MEMBER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Maryland: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["crisp_enrollment_submissions", CRISP_ENROLLMENT_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_REQUIRED],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_OPTIONAL],
  ],
  stepNames: [
    "Submitter",
    "Certification",
    "Submissions",
    "Comments",
    "Review",
  ],
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const MassGIC: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
  ],
  stepNames: [
    "Submitter",
    "Contacts",
    "Comments",
    "Review",
  ],
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Minnesota: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["legal_advisory", LEGAL_ADVISORY_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["non_claims_payments",
      {
        title: "Non-Claims Payments",
        description: "Please provide the following information regarding the person responsible for non-claims payments submissions.",
        required: true,
        showNotApplicable: true,
        showPlans: false,
        refFileTypeId: null,
      },
    ],
  ],
  stepNames: defaultStepNames,
  qualifierReasons: [
    // values must never change to preserve radio button functionality on old forms and allow trending!
    // First reason updated to include dental in DO-270 at the request of MN.
    {
      value: "Our combined lines of business (i.e., institutional, professional, dental, and pharmacy) from last year were less than $3 million paid out on claims for covered residents of Minnesota.",
    },
    {
      value: "Our pharmacy benefits manager totals paid out last year were less than $300,000 on claims for covered residents of Minnesota.",
    },
    {
      value: OTHER,
    },
  ],
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion:
    {
      question: "Is your organization a third-party administrator (TPA) or is there an organization that is contracted with your organization as a TPA?",
      options: [
        {
          value: TPAUse.WE_ARE_THE_TPA,
          label: "Yes, we are a TPA.",
          hidden: false,
          note: "Please enter information below on the health plan/insurer for whom you service as a TPA.",
          organizationNameTooltip: "If you are a TPA please use the organization name field to indicate the health plan/insurer your team administers benefits for.",
        },
        {
          value: TPAUse.WE_HAVE_A_TPA,
          label: "Yes, we have a TPA submitting data on our behalf.",
          hidden: false,
          note: "Please use the fields below to indicate the TPA submitting data on behalf of your health plan/insurer.",
          organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the TPA administering benefits on your behalf.",
        },
        {
          value: TPAUse.NO_TPA,
          label: "No, we are not a TPA nor do we have a TPA submitting on our behalf.",
          hidden: false,
        },
      ],
    },
  pbmUseQuestion: {
    question: "Is your organization a pharmacy benefits manager (PBM) or is there an organization that is contracted with your organization as a PBM?",
    options: [
      {
        value: PBMUse.WE_ARE_THE_PBM,
        label: "Yes, we are a PBM.",
        hidden: false,
        note: "Please enter information below on the health plan/insurer for whom you service as a PBM.",
        organizationNameTooltip: "If you are a PBM please use the organization name field to indicate the health plan/insurer your team administers benefits for.",
      },
      {
        value: PBMUse.WE_HAVE_A_PBM,
        label: "Yes, we have a PBM submitting data on our behalf.",
        hidden: false,
        note: "Please use the fields below to indicate the PBM submitting data on behalf of your health plan/insurer.",
        organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the PBM administering benefits on your behalf.",
      },
      {
        value: PBMUse.NO_PBM,
        label: "No, we are not a PBM nor do we have a PBM submitting on our behalf.",
        hidden: false,
      },
    ],
  },
  carveOutPayerUseQuestion: {
    question: "Is your organization a behavioral health carve-out payer or is there an organization that is contracted with your organization as a behavioral health carve-out payer?",
    options: [
      {
        value: CarveOutPayerUse.WE_ARE_THE_CARVE_OUT_PAYER,
        label: "Yes, we are a behavioral health carve-out payer.",
        hidden: false,
        note: "Please use the organization name field below to enter the name of the organization(s) on whose behalf you are submitting data.",
        organizationNameTooltip: "If you are a behavioral health carve-out payer please use the organization name field to indicate the health plan/insurer your team administers benefits for.",
      },
      {
        value: CarveOutPayerUse.WE_HAVE_A_CARVE_OUT_PAYER,
        label: "Yes, we have a behavioral health carve-out payer submitting data on our behalf.",
        hidden: false,
        note: "Please use the fields below to indicate the behavioral health carve-out payer submitting data on behalf of your health plan/insurer.",
        organizationNameTooltip: "If you are a health plan/insurer please use the organization name field to indicate the behavioral health carve-out payer administering benefits on your behalf.",
      },
      {
        value: CarveOutPayerUse.NO_CARVE_OUT_PAYER,
        label: "No, we are not a behavioral health carve-out payer nor do we have a behavioral health carve-out payer submitting on our behalf.",
        hidden: false,
      },
    ],
  },
  showTpaSelfInsuredForm: true,
}

const Nevada: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  qualifierReasons: [
    // DO-138 - values must never change!
    {
      value: "Our health plan provided coverage to fewer than 1,000 residents of the state of Nevada in accordance with Nevada Revised Statues (NRS) 439B.840",
    },
    {
      value: "Our health plan does not include any of the following members: Nevada residents, State of Nevada employees covered by a Public Employee Benefit Program, or individuals covered by a health plan issued in Nevada in the individual or small-group market regardless of their state of residence.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const OHCA: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["business", BUSINESS_DEFAULT],
    ["regulatory", REGULATORY_DEFAULT],
    ["technical", TECHNICAL_DEFAULT],
  ],
  stepNames: [
    "Submitter",
    "Contacts",
    "Submissions",
    "Review",
  ],
  contactExclusivity: false,
  hasParentCompanyName: true,
  insurerTypeRequired: true,
  hasFein: false,
  feinRequired: false,
  licenseTypeFieldLabel: "License Issuer",
  licenseTypeTooltip: "For products not offered under a CA license, you may register a single entity with license-type “Other” and use a single set of files to report spending data for CA residents in those products.",
  licenseTypes: defaultLicenseTypes,
  maxSelectedLicenseTypes: 1,
  showLicensedEntityName: false,
  // Only OHCA has participant type (Voluntary/Mandatory) instead of submitter type:
  showSubmitterType: false,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  showParticipantType: true,
  marketCategories: [
    "Commercial (Full Benefits)",
    "Commercial (Partial Benefits)",
    "Medi-Cal Managed Care",
    "Medicare Advantage",
    "Dual Eligibles (Medi-Cal Expenses Only)",
    "Dual Eligibles (Medicare Expenses Only)",
    "Dual Eligibles (Medi-Cal and Medicare Expenses)",
  ],
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Oregon: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const RhodeIsland: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  qualifierReasons: [
    // DO-134
    // values must never change to preserve radio button functionality on old forms and allow trending!
    // Edited 11/7/24 in DO-192
    {
      value: "Our health plan provided coverage to fewer than the required 3,000 members enrolled in plans that are not otherwise excluded from the files that are offered or administered by the Reporting Entity as of November 1 of this calendar year and is therefore exempt from the data submission requirements for the next calendar year.",
    },
    {
      value: "Our Rhode Island small employer health insurance plan provided coverage to fewer than the required 3,000 members across all U.S. states.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["lockbox_enrollment_submissions", LOCKBOX_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_REQUIRED],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
    ["provider_file_submissions", PROVIDER_OPTIONAL],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Vermont: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  qualifierReasons: [
    // DO-133
    // values must never change to preserve radio button functionality on old forms and allow trending!
    {
      value: "Our company provides health-insurance-related services (including medical, pharmacy, and dental services) to fewer than 200 residents of the state of Vermont across all lines of business.",
    },
    {
      value: OTHER,
    },
  ],
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["legal_advisory", LEGAL_ADVISORY_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Virginia: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["primary_data_supplier", PRIMARY_DATA_SUPPLIER_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_REQUIRED],
    ["legal_advisory", LEGAL_ADVISORY_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["provider_file_submissions", PROVIDER_DEFAULT],
    ["regulatory", REGULATORY_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["technical_support_1", TECHNICAL_SUPPORT_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

const Washington: IClientConfig = {
  fillerConfig: FILLER_DEFAULT,
  contactTypes: [
    ["program_compliance", PROGRAM_COMPLIANCE_DEFAULT],
    ["sftp", SFTP_DEFAULT],
    ["enrollment_submissions", ENROLLMENT_DEFAULT],
    ["medical_claims_submission", MEDICAL_DEFAULT],
    ["pharmacy_claims_submission", PHARMACY_DEFAULT],
    ["dental_claims_submissions", DENTAL_DEFAULT],
  ],
  stepNames: defaultStepNames,
  contactExclusivity: false,
  insurerTypeRequired: true,
  hasFein: true,
  feinRequired: true,
  showSubmitterType: true,
  submitterTypes: DEFAULT_SUBMITTER_TYPES,
  tpaUseQuestion: DEFAULT_TPA_USE_QUESTION,
  pbmUseQuestion: DEFAULT_PBM_USE_QUESTION,
  carveOutPayerUseQuestion: DEFAULT_CARVE_OUT_PAYER_USE_QUESTION,
}

export const ClientConfigs: Record<string, IClientConfig | undefined> = {
  Alaska: Alaska,
  California: California,
  Colorado: Colorado,
  Connecticut: Connecticut,
  CoveredCA: CoveredCA,
  Demonstration: Demonstration,
  Georgia: Georgia,
  HealthShareExchange: HealthShareExchange,
  Indiana: Indiana,
  IntegratedHealthAssociation: IntegratedHealthAssociation,
  Maryland: Maryland,
  MassGIC: MassGIC,
  Minnesota: Minnesota,
  Nevada: Nevada,
  OHCA: OHCA,
  Oregon: Oregon,
  RhodeIsland: RhodeIsland,
  Vermont: Vermont,
  Virginia: Virginia,
  Washington: Washington,
} as const
